// src/realtime/LayoutChart/22.jsx
import { useState, useEffect } from 'react';
import LayoutGrid from './LayoutGrid';
import ChartPanel from './ChartPanel';
import {
  Box,
  Autocomplete,
  TextField,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Favorite as FavoriteIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { tokens } from '../../../theme';
import { useTheme } from '@mui/material/styles';
import { useIsMobile, useIsPortraitTablet, useIsTablet } from '../../../components/hooks';
import { useFetchingData } from '../../../components/api.js';


// ─── FavoriteListPopup ───────────────────────────────────────────
// (Now using position: 'fixed' so it floats in the viewport,
//  and with updated styling for the “Add/Update” button.)

const FavoriteListPopup = ({
  selectedFavorite,
  setSelectedFavorite,
  symbolSelected,
  applyFavoriteSymbols,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [favorites, setFavorites] = useState([]);
  const [openSettings, setOpenSettings] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [optionToDelete, setOptionToDelete] = useState(null);
  const [editedFavoriteName, setEditedFavoriteName] = useState('');
  const [editedSymbols, setEditedSymbols] = useState([]);

  const isMobile = useIsMobile();
  const isPTablet = useIsPortraitTablet();
  const isTablet = useIsTablet();

  // 1) Load favorites from localStorage on mount
  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem('favoriteLists22')) || [];
    setFavorites(stored);
  }, []);

  // 2) Whenever favorites array changes, persist back to localStorage
  useEffect(() => {
    localStorage.setItem('favoriteLists22', JSON.stringify(favorites));
  }, [favorites]);

  // 3) Keep editedSymbols and editedFavoriteName in sync with the currently selectedFavorite
  useEffect(() => {
    if (selectedFavorite) {
      setEditedFavoriteName(selectedFavorite.name);
      setEditedSymbols([...selectedFavorite.symbols]);
    }
  }, [selectedFavorite]);

  // --- Handlers ---

  // Called when user picks or clears a favorite
  const handleFavoriteSelect = (event, value) => {
    if (!value) {
      // Cleared selection → clear all four charts
      setSelectedFavorite(null);
      applyFavoriteSymbols([]);
    } else {
      setSelectedFavorite(value);
      applyFavoriteSymbols(value.symbols);
    }
  };

  // Add a brand-new favorite or update an existing one
  const addOrUpdateFavorite = () => {
    if (selectedFavorite) {
      // We are updating an existing favorite’s name (or its symbol set)
      const updated = favorites.map((fav) =>
        fav.name === selectedFavorite.name
          ? { name: editedFavoriteName, symbols: [...symbolSelected] }
          : fav
      );
      setFavorites(updated);
      // Keep the Autocomplete “selected” on the renamed version
      const newly = updated.find((f) => f.name === editedFavoriteName);
      setSelectedFavorite(newly || null);
    } else {
      // Create a brand‐new favorite (autosized name “Favorite X”)
      const nextName = `Favorite ${favorites.length + 1}`;
      const newFav = { name: nextName, symbols: [...symbolSelected] };
      setFavorites([...favorites, newFav]);
      setSelectedFavorite(null);
    }
    setEditedFavoriteName('');
    setEditedSymbols([]);
  };

  // Open “Rename / Edit” dialog for a given favorite
  const handleOpenSettings = (fav) => {
    setSelectedFavorite(fav);
    setEditedFavoriteName(fav.name);
    setEditedSymbols(fav.symbols);
    setOpenSettings(true);
  };

  // Save the edits from the dialog back into the favorites array
  const handleSaveSettings = () => {
    const updated = favorites.map((fav) =>
      fav.name === selectedFavorite.name
        ? { name: editedFavoriteName, symbols: [...editedSymbols] }
        : fav
    );
    setFavorites(updated);
    // Keep Autocomplete selection on the renamed entry
    const newly = updated.find((f) => f.name === editedFavoriteName);
    setSelectedFavorite(newly || null);
    setOpenSettings(false);
  };

  // Open “Delete confirmation” dialog for a given favorite
  const handleOpenDeleteConfirm = (fav) => {
    setOptionToDelete(fav);
    setOpenDeleteConfirm(true);
  };

  // Remove that favorite from our array (and clear symbols if it was selected)
  const handleDeleteFavorite = () => {
    const filtered = favorites.filter((fav) => fav.name !== optionToDelete.name);
    setFavorites(filtered);
    if (selectedFavorite?.name === optionToDelete.name) {
      setSelectedFavorite(null);
      applyFavoriteSymbols([]);
    }
    setOpenDeleteConfirm(false);
  };

  return (
    <>
      {/* ─── This Box is now position: 'fixed' to float above everything ─── */}
      <Box
        sx={{
          position: 'fixed',
          top: isMobile ? 8 : 16,
          right: isMobile ? 8 : 24,
          display: 'flex',
          flexDirection: 'row',
          gap: 1,
          zIndex: 1200,
        }}
      >
        <Autocomplete
          disablePortal
          id="favoriteList22"
          size="small"
          options={favorites}
          value={selectedFavorite}
          getOptionLabel={(option) => option.name}
          onChange={handleFavoriteSelect}
          renderOption={(props, option) => (
            <li
              {...props}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: theme.palette.neutral.dark,
                color: colors.white,
              }}
            >
              <span>{option.name}</span>
              <Box>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenSettings(option);
                  }}
                >
                  <EditIcon fontSize="small" sx={{ color: colors.white }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenDeleteConfirm(option);
                  }}
                >
                  <DeleteIcon fontSize="small" sx={{ color: colors.redChart[100] }} />
                </IconButton>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Favorites"
              InputProps={{
                ...params.InputProps,
                sx: {
                  backgroundColor: theme.palette.neutral.dark,
                  color: colors.white,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.neutral.dark,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.neutral.dark,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: colors.greenChart[100],
                  },
                },
              }}
            />
          )}
          sx={{
            width: isMobile ? 140 : 220,
            backgroundColor: theme.palette.neutral.dark,
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: theme.palette.neutral.dark,
              },
              '&:hover fieldset': {
                borderColor: theme.palette.neutral.dark,
              },
              '&.Mui-focused fieldset': {
                borderColor: colors.greenChart[100],
              },
            },
            '& .MuiAutocomplete-popupIndicator': {
              color: colors.white,
            },
          }}
        />

        <Button
          variant="contained"
          startIcon={selectedFavorite ? <SaveIcon /> : <FavoriteIcon />}
          onClick={addOrUpdateFavorite}
          sx={{
            bgcolor: '#FF5E48', // Pink/red like original “ADD” button
            color: '#fff',
            '&:hover': { bgcolor: '#e04a36' },
          }}
        >
          {selectedFavorite ? 'UPDATE' : 'ADD'}
        </Button>
      </Box>

      {/* ── EDIT (Rename) DIALOG ──────────────────────── */}
      <Dialog open={openSettings} onClose={() => setOpenSettings(false)}>
        <DialogTitle>Edit Favorite</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            fullWidth
            value={editedFavoriteName}
            onChange={(e) => setEditedFavoriteName(e.target.value)}
            sx={{
              mt: 1,
              backgroundColor: theme.palette.neutral.dark,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.neutral.dark,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.neutral.dark,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.greenChart[100],
              },
            }}
          />
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ mt: 2, display: 'block' }}
          >
            This will overwrite the current symbols (in all four charts) under that name.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSettings(false)}>Cancel</Button>
          <Button onClick={handleSaveSettings} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── DELETE CONFIRMATION DIALOG ────────────────── */}
      <Dialog open={openDeleteConfirm} onClose={() => setOpenDeleteConfirm(false)}>
        <DialogTitle>Delete Favorite</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{optionToDelete?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteConfirm(false)}>Cancel</Button>
          <Button onClick={handleDeleteFavorite} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};


// ─── Layout22 (2×2) ─────────────────────────────────────────────────
export default function Layout22() {
  const { __fetching_data__ } = useFetchingData();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Keep four symbols in state (one per chart)
  const [symbols, setSymbols] = useState(Array(4).fill(null));
  const [options, setOptions] = useState([]);

  // Favorite‐list UI state
  const [selectedFavorite, setSelectedFavorite] = useState(null);
  


  

  // Whenever a favorite‐list is chosen, override all four chart symbols at once
  const applyFavoriteSymbols = (favArray) => {
    // If the favorite array has fewer than 4 entries, pad with null
    const arr = [
      favArray[0] || null,
      favArray[1] || null,
      favArray[2] || null,
      favArray[3] || null,
    ];
    setSymbols(arr);
  };

  // The base URL for your fetch calls (unchanged)
  const baseApiUrl =
    process.env.NODE_ENV === 'production'
      ? window.location.protocol === 'https:'
        ? process.env.REACT_APP_API_HTTPS_URL
        : process.env.REACT_APP_API_HTTP_URL
      : '/application';

  // Fetch the master list of symbols (once)
  useEffect(() => {
    __fetching_data__({ url:`${baseApiUrl}/realtime_symbols` })
      .then(r=>setOptions(r.data.symbols))
      .catch(console.error);
  }, []);  
  
  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      {/* ── FAVORITES POPUP (fixed) ─────────────────────────────── */}
      <FavoriteListPopup
        selectedFavorite={selectedFavorite}
        setSelectedFavorite={setSelectedFavorite}
        symbolSelected={symbols}
        applyFavoriteSymbols={applyFavoriteSymbols}
      />

      {/* ── 2×2 GRID OF CHARTS ───────────────────────────────────── */}
      <LayoutGrid columns={2} gap={1}>
        {symbols.map((sym, idx) => (
          <ChartPanel
            key={idx}
            symbol={sym}
            symbolsList={options}
            baseApiUrl={baseApiUrl}
            onSymbolChange={(v) =>
              setSymbols((prev) => {
                const copy = [...prev];
                copy[idx] = v;
                return copy;
              })
            }
          />
        ))}
      </LayoutGrid>
    </Box>
  );
}
